import { useId } from 'react';
import { cx } from './ui';

/*
 * The platform's infinity, exactly as supplied (Reetal brand guide, "EE
 * infinity"): the outer loop on its own, so a light can travel it, and the
 * two counters of the "e"s.
 */
const LOOP =
  'M779.5,139.58h.18C759.2,59.39,686.51.09,599.96.02l-1.44.12c-.4.06-1.1.12-1.11.22-45.76.26-88.39,17.97-120.67,45.68-2.13,1.82-4.11,3.84-5.93,5.96-.94,1.09-1.03,1.31-1.44,1.73l-196.37,216.1c-1.33,1.46-6.24,6.78-7.73,8.07-21.31,18.59-49.14,29.87-79.65,29.87-58.27,0-106.94-41.1-118.61-95.9l211.94-.23c1.91-2.04.18-.21,1.84-1.95l34.83-38.34c-.06-.17,45.8-50.31,45.73-50.48C334.25,48.59,262.12-2.65,179.86.11,79.68,3.47-.21,86.63,0,186.86c.21,102.35,83.24,185.24,185.63,185.24,47.24,0,90.36-17.66,123.13-46.74.03.03.05.05.08.09.74-.62,1.58-1.45,2.38-2.19.67-.62,1.34-1.24,1.99-1.87,1.66-1.63,3.22-3.05,4.93-4.77,2.04-2.07,6.14-7.27,8.18-9.34l189.87-208.81c.87-.96,1.77-1.85,2.73-2.69,9.12-8.02,24.86-19.72,46.18-25.95,3.08-.91,14.83-4.18,30.81-4.93,1.28-.04,2.57-.06,3.87-.06,58.27,0,106.94,41.1,118.61,95.9l-211.94.06c-1.91,2.04-.18.21-1.84,1.95l-34.82,38.5c.06.17-45.8,50.31-45.73,50.48,27.09,72.25,99.17,123.46,181.37,120.76,98.83-3.26,178.27-84.29,179.95-183.16.3-17.22-1.76-33.91-5.87-49.79v.03Z';
const COUNTERS =
  'M185.62,65.21c57.94,0,106.37,40.62,118.41,94.94H67.21c12.04-54.32,60.47-94.94,118.41-94.94ZM599.77,307.43c-57.94,0-106.37-40.62-118.41-94.94h236.82c-12.04,54.32-60.47,94.94-118.41,94.94Z';

/**
 * The platform's signature at the foot of the menu (ADR-124).
 *
 * The mark drawn faint, and a short light that travels its loop and never
 * stops — "one loop, endless" — with a softer echo half a turn behind. It is
 * decoration and nothing else: hidden from assistive technology, deaf to the
 * pointer, never focusable, and still for anyone who asked for less motion.
 * Its colours come from the ramp in force, so a store's own colour (ADR-123)
 * reaches it too. It never stands in for the store's mark.
 */
export function InfinityMark({ className }: { className?: string }) {
  const id = useId().replace(/:/g, '');

  return (
    <svg
      viewBox="0 0 785.41 372.6"
      aria-hidden
      focusable="false"
      className={cx('pointer-events-none overflow-visible select-none', className)}
    >
      <defs>
        <linearGradient id={`${id}-light`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--color-brand-500)' }} />
          <stop offset="1" style={{ stopColor: 'var(--color-brand-300)' }} />
        </linearGradient>
        <filter id={`${id}-glow`} x="-10%" y="-20%" width="120%" height="140%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
      </defs>

      {/* The mark itself, barely there — it breathes slowly with the light. */}
      <path d={LOOP + COUNTERS} fillRule="evenodd" className="infinity-breathe fill-nav-ink/[0.07]" />

      {/* The light: a glow beneath, a crisp line on top, an echo behind. */}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path
          d={LOOP}
          pathLength={100}
          stroke={`url(#${id}-light)`}
          strokeWidth={26}
          strokeDasharray="16 84"
          filter={`url(#${id}-glow)`}
          className="infinity-travel opacity-70"
        />
        <path d={LOOP} pathLength={100} stroke={`url(#${id}-light)`} strokeWidth={12} strokeDasharray="16 84" className="infinity-travel" />
        <path
          d={LOOP}
          pathLength={100}
          stroke={`url(#${id}-light)`}
          strokeWidth={8}
          strokeDasharray="7 93"
          className="infinity-travel opacity-40 [animation-delay:-4.5s]"
        />
      </g>
    </svg>
  );
}
